import React, { useRef, useEffect, useState } from 'react'
import { Chip, Container, Spacer } from '@/src/ct-design/lib'
import { TravellerDetails } from '@/src/types/visa/visaItinerary'
import Downarrow from '@/src/assets/svgs/downarrow'

type TravellerSelectorType = {
    selectedTravellerIndex: number
    adultCount: number
    travellerCount: number
    travellerDetails: TravellerDetails[]
    onTravellerClick: (index: number) => void
}

const TravellerSelector = ({
    selectedTravellerIndex,
    adultCount,
    travellerCount,
    travellerDetails,
    onTravellerClick
}: TravellerSelectorType) => {
    const scrollRef = useRef<HTMLDivElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const [hasOverflow, setHasOverflow] = useState(false)
    useEffect(() => {
        const checkOverflow = () => {
            const element = scrollRef.current
            if (element) {
                const hasContentOverflow =
                    element.scrollWidth > element.clientWidth
                setHasOverflow(hasContentOverflow)
            }
        }

        checkOverflow()
        window.addEventListener('resize', checkOverflow)
        const timers = [
            setTimeout(checkOverflow, 100),
            setTimeout(checkOverflow, 500),
            setTimeout(checkOverflow, 1000)
        ]
        const observer = new MutationObserver(checkOverflow)
        if (scrollRef.current) {
            observer.observe(scrollRef.current, {
                childList: true,
                subtree: true,
                attributes: true,
                characterData: true
            })
        }

        return () => {
            window.removeEventListener('resize', checkOverflow)
            timers.forEach((timer) => clearTimeout(timer))
            observer.disconnect()
        }
    }, [travellerCount, travellerDetails])
    useEffect(() => {
        const element = scrollRef.current
        if (element) {
            const handleScroll = () => {
                if (element.scrollWidth > element.clientWidth) {
                    setHasOverflow(true)
                }
            }
            element.addEventListener('scroll', handleScroll)
            return () => element.removeEventListener('scroll', handleScroll)
        }
    }, [])

    const scrollLeft = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: -200, behavior: 'smooth' })
        }
    }

    const scrollRight = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' })
        }
    }

    const forceOverflowCheck = () => {
        const element = scrollRef.current
        if (element) {
            setHasOverflow(element.scrollWidth > element.clientWidth)
        }
    }

    useEffect(() => {
        const timer = setTimeout(forceOverflowCheck, 50)
        return () => clearTimeout(timer)
    })

    return (
        <Container className='relative w-full flex flex-row' ref={containerRef}>
            {hasOverflow && (
                <>
                    <Container
                        className='flex flex-middle'
                        style={{ transform: 'rotate(90deg)' }}
                        onClick={scrollLeft}
                    >
                        <Downarrow />
                    </Container>
                    <Spacer className='ml-1' />
                </>
            )}
            <Container
                ref={scrollRef}
                style={{
                    display: 'flex',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    whiteSpace: 'nowrap',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    flexGrow: 1,
                    width: '0'
                }}
            >
                {Array.from({ length: travellerCount }).map((_, index) => {
                    const traveller = travellerDetails[index]
                    const { firstName } = traveller?.personalDetails || {}
                    const { paxType } = {
                        paxType: traveller?.personalDetails.paxType || 'ADULT'
                    }
                    const label = firstName
                        ? firstName
                        : paxType === 'ADULT'
                          ? `Adult ${index + 1}`
                          : `Child ${index + 1 - adultCount}`

                    return (
                        <Container
                            key={index}
                            style={{
                                display: 'inline-block',
                                marginRight: '8px',
                                flexShrink: 0
                            }}
                        >
                            <Chip
                                key={index}
                                label={label}
                                onClick={() => onTravellerClick(index)}
                                isSelected={selectedTravellerIndex === index}
                            />
                        </Container>
                    )
                })}
            </Container>
            {hasOverflow && (
                <>
                    <Spacer className='ml-1' />
                    <Container
                        className='flex flex-middle'
                        onClick={scrollRight}
                        style={{ transform: 'rotate(-90deg)' }}
                    >
                        <Downarrow />
                    </Container>
                </>
            )}
        </Container>
    )
}

export default TravellerSelector
